import type RapierNS from '@dimforge/rapier3d-compat'
import {
  comprimento as comprimento3,
  girarEmTorno,
  somar,
  subtrair,
  vetor,
  type Vetor3,
} from '../dominio/geometria/vetor3'
import { energiaDeMassa, type CorpoSolto } from '../dominio/didatico/cargaSolta'
import {
  avaliarPossivelAprisionamento,
  type EvidenciaAprisionamento,
  type MembroAprisionavel,
} from '../dominio/didatico/aprisionamentoMembro'
import {
  ID_ALVO_EXTERNO,
  ID_ALVO_EXTERNO_SECUNDARIO,
  type TipoAlvoImpacto,
} from '../dominio/didatico/alvosImpacto'
import {
  colisoresDoHabitaculo,
  type ColisorHabitaculo,
} from '../dominio/didatico/colisoresHabitaculo'
import type { MapaEstados } from '../dominio/didatico/estadoPecas'
import {
  retencaoDaAncora,
  type AncoraOcupante,
  type PerfilVeiculo,
} from '../dominio/didatico/perfilVeiculo'
import { pontosDoCinto } from '../dominio/didatico/pontosCinto'
import { modulosSrs, type ChaveSrs } from '../dominio/didatico/pontosSrs'
import {
  ajustePosImpactoA,
  velocidadeAjusteA,
  type ColisaoEntreVeiculos,
} from '../dominio/didatico/colisaoEntreVeiculos'
import type { ProporcoesVitima } from '../dominio/didatico/perfisVitima'
import { POSE_SENTADA, posicaoCabeca, eixoColunaDaVitima } from '../dominio/didatico/rigVitima'
import { reacaoDoOcupante, type ModoRetencao } from '../dominio/didatico/retencaoOcupante'
import {
  origemDeContatoPadrao,
  superficiesDoHabitaculo,
} from '../dominio/didatico/superficiesInternas'
import {
  TEMPO_PRETENSIONADOR,
  amostrarCinematica,
  colisao,
  doMundo,
  duracaoRealS,
  instanteDoContato,
  instantesDeContato,
  paraMundo,
  taxaDeRolagemRadPorS,
  velocidadeDoEvento,
  velocidadeNoPonto,
  type TipoColisao,
} from '../dominio/didatico/timelineCinematica'
import { dentroDoEnvelope } from '../dominio/didatico/cargaSoltaColocacao'
import {
  QUATERNION_IDENTIDADE,
  idSegmentoRagdoll,
  type Quaternion,
  type SegmentoRagdoll,
  type QuadroCorpo,
  type TrajetoriaCorpo,
} from './trajetoria'
import {
  CONFIG_TONUS,
  calcularAnguloCervical,
  avaliarEstresseCervical,
  type TonusOcupante,
  type LeituraBiomecanicaInstante,
} from '../dominio/didatico/tonusOcupante'

/**
 * Resolve o movimento dos ocupantes e objetos com física real (Rapier), UMA
 * vez, fora do laço de desenho.
 *
 * Por que não a integração procedural de `retencaoOcupante.ts`: aquela trata a
 * carga como um ponto que anda em linha reta na direção do tronco do motorista,
 * sem gravidade, sem rotação e sem volume. Um cachorro no banco traseiro e um
 * extintor no assoalho seguiam a mesma reta, e a massa do catálogo não tinha
 * efeito nenhum. Aqui cada corpo tem massa, volume e orientação, cai, tomba,
 * ricocheteia e é detido pelo que houver no caminho — inclusive pela vítima.
 *
 * Por que uma resolução única em vez de integrar a cada quadro:
 *
 * - a régua de tempo da aula é arrastável nos dois sentidos, e física não roda
 *   para trás. Com a trajetória tabelada, voltar é reler;
 * - o mesmo acidente repete a mesma pose, que é a objeção registrada no §45.1
 *   do ACHADOS contra usar uma engine — resolvida aqui em vez de aceita;
 * - nenhum quadro de desenho paga custo de simulação.
 *
 * O tempo simulado é o tempo REAL do evento (`duracaoRealS`), não os 4,5 s
 * lentos da reprodução: integrar a gravidade no tempo de reprodução faria a
 * carga cair dezenas de metros dentro do habitáculo.
 */

export const GRAVIDADE = 9.81

/**
 * Passos de simulação da sequência inteira.
 *
 * Alto por duas razões, não por precisão gratuita. A resolução temporal da
 * tabela é o que decide a fluidez da reprodução — 540 quadros espalhados nos
 * 4,5 s de aula dão 120 estados por segundo. E o passo curto evita travessia:
 * a 50 km/h um passo de 1/60 s avançaria 23 cm, atravessando chapas de 4 cm.
 */
export const PASSOS_PADRAO = 540

/** Atrito e restituição do interior: nada aqui é medição, é comportamento legível. */
const ATRITO_INTERIOR = 0.7
const RESTITUICAO_INTERIOR = 0.15

/**
 * Bolsa de airbag como colisor do habitáculo.
 *
 * O colisor nasce quando a bolsa está praticamente cheia e some quando ela já
 * esvaziou pelos respiros: é nessa janela que ela segura a cabeça e o tórax.
 * A caixa é menor que a bolsa desenhada porque a bolsa real cede ao ser
 * carregada — o corpo afunda nela antes de parar.
 */
const EXPANSAO_AIRBAG_ATIVO = 0.8
const EXPANSAO_AIRBAG_ESVAZIADO = 0.4
const FATOR_COMPRESSAO_AIRBAG = 0.75
const RESTITUICAO_AIRBAG = 0.05
const ATRITO_AIRBAG = 0.4
const ATRITO_CARGA = 0.5
const RESTITUICAO_CARGA = 0.2

/**
 * Queda mínima de velocidade relativa, em km/h, dentro de `JANELA_IMPACTO_S`.
 *
 * Impacto não é "estar tocando": a carga está encostada em algo desde o primeiro
 * passo — no assoalho do porta-malas, no assento, no tampo do painel. Duas
 * tentativas erraram por aí. Registrar o primeiro contato dava 0 km/h e 0 J, o
 * contato de apoio no passo zero; exigir velocidade relativa mínima só atrasava o
 * mesmo erro, porque o contato com o assoalho disparava assim que a carga
 * começava a escorregar.
 *
 * O que caracteriza a batida é a DESACELERAÇÃO: o instante em que o corpo perde
 * velocidade de repente é o instante em que encontrou algo que o detém. Assim o
 * critério não precisa classificar superfícies em "apoia" e "detém" — a mesma
 * chapa faz os dois papéis conforme a direção do movimento.
 */
const QUEDA_MINIMA_IMPACTO_KMH = 8

/**
 * Janela de tempo REAL sobre a qual a queda de velocidade é medida.
 *
 * Medir a queda de um passo para o outro parecia equivalente e não é: o passo é
 * uma escolha de discretização, e o solver espalha a resolução de um contato por
 * vários passos. Com 540 passos num evento de 0,7 s, uma batida que tira 15 km/h
 * em 5 passos aparecia como 3 km/h por passo e passava batido. Sobre uma janela
 * de tempo fixa o critério é uma desaceleração — grandeza física — e não muda de
 * significado se alguém ajustar `PASSOS_PADRAO`.
 */
const JANELA_IMPACTO_S = 0.01

/**
 * Quão alinhado ao movimento um contato precisa estar para ser o alvo da batida.
 *
 * 1 é exatamente à frente, 0 é de lado. O corpo está sempre em contato com o que
 * o APOIA — assento, assoalho — e, na maquete, a cabeça do adulto sentado fica a
 * 3,5 cm do teto. Sem este piso, o alvo reportado era o apoio, não o obstáculo:
 * o motorista sem cinto batia no volante e o painel dizia "assento".
 */
const ALINHAMENTO_MINIMO_ALVO = 0.4

/**
 * Teto de plausibilidade da velocidade relativa, como múltiplo da velocidade de
 * impacto declarada.
 *
 * Um corpo solto dentro do veículo não pode estar mais rápido, em relação a ele,
 * do que a variação de velocidade que o próprio veículo sofre — mais uma folga
 * para a componente de rotação. Acima disso não é batida: é pico de penetração
 * do solver, que acontece quando a lataria em rotação passa por dentro de um
 * corpo que não gira junto com ela.
 *
 * Sem este teto, o tombamento reportava 1508 km/h e 2,3 milhões de
 * quilogramas-força — número que destruiria a confiança do instrutor em todos os
 * outros.
 *
 * Os quilogramas-força altos que SOBRAM não são artefato: 78 kg a 37 km/h dão
 * mesmo mais de uma tonelada-força equivalente, que é a própria lição.
 */
const FATOR_VELOCIDADE_PLAUSIVEL = 1.5

/**
 * Folga do cinto, em metros: quanto o corpo anda antes de a fita entrar em carga.
 *
 * É o que faz a curva com cinto coincidir com a livre no começo e divergir
 * depois — a mesma propriedade que o §45.2 do ACHADOS obteve por integração,
 * aqui obtida por vínculo. Por isso o cinto é modelado como CORDA e não como
 * mola: corda impede passar de um comprimento e deixa a folga livre abaixo dele,
 * que é o comportamento de uma fita.
 */
const FOLGA_CINTO_M = 0.09

/** A faixa diagonal trabalha mais justa que as pélvicas. */
const FRACAO_FOLGA_DIAGONAL = 0.3

/** Quanto o pretensionador recolhe da folga, em metros. */
const RECOLHIMENTO_PRETENSIONADOR_M = 0.05

/**
 * Assentamento: o boneco SENTA antes de o evento começar.
 *
 * É a correção do defeito que o instrutor descreveu como "assim que ele inicia o
 * deslocamento dentro do carro, já machuca". A causa medida não era a colisão:
 * era o NASCIMENTO do boneco. Treze corpos rígidos, ligados por juntas
 * esféricas, criados numa pose autoral que não é equilíbrio e com sobreposições
 * de poucos milímetros contra assento, assoalho e parede corta-fogo. O Rapier
 * resolve isso no primeiro passo, e não economiza: medido na frontal, pés a 640 e
 * 752 km/h em relação ao carro, coxas a 281 e 317, pelve a 219 — tudo no passo
 * zero, com o veículo ainda a velocidade constante. O que sobrava abaixo do teto
 * de plausibilidade virava impacto REPORTADO contra o para-brisa, o volante e a
 * pedaleira nos primeiros 2% do evento: oito leituras falsas na frontal, nove no
 * capotamento, antes de o veículo encostar em coisa alguma.
 *
 * Tentar corrigir cada sobreposição na pose inicial foi a primeira saída, e é a
 * errada: as cotas dos pés são calibração deliberada — eles se apoiam no assoalho
 * junto à pedaleira, e é disso que depende a leitura do cinto —, e mexer nelas
 * troca um defeito por outro. Além de não escalar: qualquer mudança futura na
 * pose ou no habitáculo traria o transiente de volta.
 *
 * O que um laboratório de impacto faz é o que este trecho faz: o manequim é
 * sentado e ACOMODADO antes de o trenó disparar. Com o veículo parado no instante
 * zero e amortecimento alto — que aqui é legítimo, porque acomodar não é o evento
 * —, as expulsões se dissipam e o corpo encontra o próprio apoio. Só então ele
 * recebe a velocidade do veículo e o evento começa a ser gravado.
 */
const PASSOS_ASSENTAMENTO = 120

/** Passo do assentamento, fixo: a pose acomodada não pode depender de `passos`. */
const PASSO_ASSENTAMENTO_S = 1 / 240

/** Amortecimento durante o assentamento — só para dissipar, nunca durante o evento. */
const ATENUACAO_ASSENTAMENTO = 12

/**
 * Atrito do corpo contra banco e chapas.
 *
 * 0,45, e não 0,8. O Rapier combina os dois coeficientes pela média, então 0,8
 * contra os 0,7 do interior dava 0,75 — borracha sobre borracha. Um corpo
 * vestido sobre estofado fica perto de 0,45, que combinado com o interior dá
 * 0,575.
 *
 * A diferença não é cosmética: a 0,8 o ocupante sem cinto ficava agarrado ao
 * banco durante a rolagem e a pelve dele parava a meio caminho do teto (y=0,91
 * numa cabine que vai de 0,30 a 1,495). A 0,45 ela chega a 1,30 — deitada sobre
 * o teto que virou piso, que é a lição do capotamento sem cinto. Com cinto, a
 * pelve fica em 0,66 nos dois casos: quem segura ali é a fita, não o atrito.
 */
const ATRITO_CORPO = 0.45
const RESTITUICAO_CORPO = 0.05

/**
 * Atenuação linear dos corpos: ZERO, e o zero é decisão, não esquecimento.
 *
 * Atenuação no Rapier age sobre a velocidade de MUNDO — é arrasto de ar. Dentro
 * de uma cabine fechada não há vento, e o valor que estava aqui (0,48) tirava
 * 11,7·(1−e^(−0,48·0,712)) ≈ 3,4 m/s, ou 12 km/h, do corpo em relação ao carro na
 * colisão frontal, SEM que ele encostasse em nada. No capotamento, mais longo,
 * tirava metade da velocidade. Era exatamente o que o instrutor descrevia como
 * "ele não está na velocidade do carro", e era também o que prendia o ocupante
 * sem cinto no banco durante a rolagem em vez de deixá-lo cair sobre o teto.
 *
 * Quem segura o corpo passa a ser o que de fato o segura na vida: o atrito de
 * contato, as juntas do ragdoll e o cinto.
 */
const ATENUACAO_LINEAR_CORPO = 0

/**
 * Atenuação angular: 0,2, e não os 5,2 de antes.
 *
 * Rotação própria do corpo não depende de referencial, então aqui a atenuação não
 * é errada — só estava alta demais. A 5,2, depois dos 0,6 s da rolagem sobrava
 * e^(−3,1) ≈ 4% da rotação: o corpo simplesmente NÃO girava junto com a lataria,
 * que é o mecanismo por trás dos picos de penetração que o §48.3 do ACHADOS
 * registrou. A 0,2 sobra e^(−0,12) ≈ 89%.
 */
const ATENUACAO_ANGULAR_CORPO = 0.2

export interface CargaParaResolver {
  /** id do ocupante, para reencontrar a trajetória na cena */
  id: string
  corpo: CorpoSolto
  /** posição inicial no referencial do veículo, já empacotada */
  origem: Vetor3
}

export interface PessoaParaResolver {
  id: string
  ancora: AncoraOcupante
  proporcoes: ProporcoesVitima
  retencao: ModoRetencao
}

/** O ocupante terminou fora do envelope do veículo. */
export interface EjecaoOcupante {
  id: string
  nome: string
  /** instante normalizado em que o corpo cruzou o envelope */
  tempo: number
  /** por onde saiu, quando dá para dizer */
  porOnde: string
}

export interface EntradaSolucionador {
  perfil: PerfilVeiculo
  estados: MapaEstados
  tipoColisao: TipoColisao
  alvoImpacto?: TipoAlvoImpacto
  /** velocidade do evento escolhida pelo aluno; omitida, vale a declarada pelo tipo */
  velocidadeImpactoKmh?: number
  cargas: readonly CargaParaResolver[]
  pessoas: readonly PessoaParaResolver[]
  passos?: number
  tonus?: TonusOcupante
  /**
   * Colisão contra outro veículo já resolvida. A timeline do veículo é a de uma
   * barreira; com isto, o veículo da física também leva o movimento que a troca
   * de quantidade de movimento lhe deu — e o ocupante sente o ΔV certo.
   */
  colisaoEntreVeiculos?: ColisaoEntreVeiculos | null
}

export interface EstresseBiomecanicoOcupante {
  idOcupante: string
  quadros: readonly LeituraBiomecanicaInstante[]
  picoAnguloCervical: number
  tempoPico: number
  houveSobrecargaArticular: boolean
}

export interface ImpactoCarga {
  /** id do ocupante (a carga) que bateu */
  id: string
  nomeCorpo: string
  /** nome do que ela encontrou: uma peça do habitáculo ou uma pessoa */
  alvo: string
  /** instante normalizado 0..1, na mesma régua da timeline */
  tempo: number
  /** velocidade relativa ao veículo imediatamente ANTES de ser detida, em km/h */
  velocidadeRelativaKmh: number
  joules: number
  pesoEquivalenteKgf: number
}

export interface AprisionamentoMembro extends EvidenciaAprisionamento {
  id: string
  idOcupante: string
  tempo: number
}

export interface ResultadoFisica {
  trajetorias: TrajetoriaCorpo[]
  /** movimento físico do veículo-alvo ou pose estática do poste/muro */
  trajetoriaAlvo: TrajetoriaCorpo | null
  /** movimento físico do segundo alvo externo, no engavetamento (`impactoSecundario`) */
  trajetoriaAlvoSecundario: TrajetoriaCorpo | null
  /** membros mantidos em zonas de retenção por contato sustentado */
  aprisionamentos: AprisionamentoMembro[]
  /** ejeções medidas: o corpo terminou fora do envelope, não estimadas */
  ejecoes: EjecaoOcupante[]
  /** primeiro impacto de cada carga, na ordem em que aconteceram */
  impactos: ImpactoCarga[]
  /** leituras de estresse biomecânico por ocupante */
  estresseBiomecanico: Record<string, EstresseBiomecanicoOcupante>
  /** passo de integração usado, em segundos */
  passoS: number
  /** duração real do evento simulado, em segundos */
  duracaoS: number
}

const paraVetorRapier = (v: Vetor3) => ({ x: v.x, y: v.y, z: v.z })

/** Quaternion que leva +Y até `eixo`, para orientar cápsulas ao longo da coluna. */
function quaternionDeYPara(eixo: Vetor3): Quaternion {
  const comprimentoEixo = comprimento3(eixo)
  if (comprimentoEixo < 1e-9) return QUATERNION_IDENTIDADE
  const d = vetor(eixo.x / comprimentoEixo, eixo.y / comprimentoEixo, eixo.z / comprimentoEixo)
  // produto vetorial de +Y com o eixo dá o eixo de rotação; o escalar vem do cosseno
  const w = 1 + d.y
  if (w < 1e-9) return { x: 0, y: 0, z: 1, w: 0 }
  const q = { x: d.z, y: 0, z: -d.x, w }
  const norma = Math.hypot(q.x, q.y, q.z, q.w)
  return { x: q.x / norma, y: q.y / norma, z: q.z / norma, w: q.w / norma }
}

/** Quaternion de uma rotação em torno de +X. */
function quaternionEmX(anguloRad: number): Quaternion {
  return { x: Math.sin(anguloRad / 2), y: 0, z: 0, w: Math.cos(anguloRad / 2) }
}

/** Quaternion de uma rotação em torno de +Z — a rolagem do capotamento. */
function quaternionEmZ(anguloRad: number): Quaternion {
  return { x: 0, y: 0, z: Math.sin(anguloRad / 2), w: Math.cos(anguloRad / 2) }
}

/** Quaternion de uma rotação em torno de +Y. */
function quaternionEmY(anguloRad: number): Quaternion {
  return { x: 0, y: Math.sin(anguloRad / 2), z: 0, w: Math.cos(anguloRad / 2) }
}

/** Descrição de colisor do habitáculo, no referencial do corpo do veículo. */
function descreverColisor(R: typeof RapierNS, colisor: ColisorHabitaculo) {
  return R.ColliderDesc.cuboid(
    colisor.meiaExtensao.x,
    colisor.meiaExtensao.y,
    colisor.meiaExtensao.z,
  )
    .setTranslation(colisor.centro.x, colisor.centro.y, colisor.centro.z)
    .setRotation(quaternionEmX(colisor.inclinacaoXRad))
}

/**
 * Volumes que representam uma pessoa sentada: pelve, tronco, cabeça e coxas.
 *
 * Derivados das proporções e do eixo da coluna que o resto do projeto já usa
 * (`eixoColunaDaVitima`, `posicaoCabeca`), e não da malha de `VitimaProcedural`:
 * são quatro volumes grosseiros, o suficiente para deter um objeto e dizer que
 * ele bateu na vítima. A alternativa — colisor por segmento da malha — daria
 * precisão que a aula não usa, ao preço de uma segunda verdade sobre a pose.
 *
 * CUBOIDES nas proporções reais, e não cápsulas. Enquanto o ocupante era corpo
 * cinemático, a forma quase não importava: um corpo cinemático atravessa o banco
 * sem reclamar. Como corpo dinâmico, uma cápsula de raio `larguraPelve/2` desce
 * 18 cm abaixo do assento, nasce enterrada nele e é expulsa no primeiro passo —
 * o ocupante subia 14 cm e não saía do lugar.
 */
function volumesDaPessoa(
  pessoa: PessoaParaResolver,
  deslocamento: Vetor3,
  inclinacaoTroncoRad: number,
): Array<{
  id: string
  forma: 'cuboide' | 'esfera'
  centro: Vetor3
  rotacao: Quaternion
  meiaExtensao: Vetor3
}> {
  const p = pessoa.proporcoes
  const colocacao = {
    posicaoPelve: somar(pessoa.ancora.posicao, deslocamento),
    guinadaRad: pessoa.ancora.rotacao.y,
    reclinacaoRad: 0,
  }
  const pose = { ...POSE_SENTADA, troncoInclinacaoRad: inclinacaoTroncoRad }
  const eixo = eixoColunaDaVitima(colocacao, p, pose)
  const direcaoTronco = subtrair(eixo.topoColuna, eixo.baseColuna)
  const rotacaoTronco = quaternionDeYPara(direcaoTronco)

  // a pelve vai do alto dela até o TOPO do assento, sem afundar: o manequim
  // didático tem a pelve 1 cm dentro da almofada, e como corpo dinâmico isso
  // vira penetração resolvida a cada passo — o corpo subia 2 cm e a cabeça
  // passava a raspar no teto
  const topoPelve = colocacao.posicaoPelve.y + p.alturaPelve / 2
  const basePelve = Math.max(pessoa.ancora.alturaAssento, colocacao.posicaoPelve.y - p.alturaPelve / 2)

  return [
    {
      id: 'pelve',
      forma: 'cuboide',
      centro: vetor(colocacao.posicaoPelve.x, (topoPelve + basePelve) / 2, colocacao.posicaoPelve.z),
      rotacao: QUATERNION_IDENTIDADE,
      meiaExtensao: vetor(
        p.larguraPelve / 2,
        Math.max((topoPelve - basePelve) / 2, 0.02),
        p.profundidadeTronco / 2,
      ),
    },
    {
      id: 'tronco',
      forma: 'cuboide',
      centro: somar(eixo.baseColuna, {
        x: direcaoTronco.x / 2,
        y: direcaoTronco.y / 2,
        z: direcaoTronco.z / 2,
      }),
      rotacao: rotacaoTronco,
      meiaExtensao: vetor(p.larguraTronco / 2, p.alturaTronco / 2, p.profundidadeTronco / 2),
    },
    {
      // esfera, e não caixa: os cantos de um cubo de meia-aresta `raioCabeca`
      // avançam 16 cm na diagonal e encostavam no teto com a vítima sentada
      id: 'cabeca',
      forma: 'esfera',
      centro: posicaoCabeca(colocacao, p, pose),
      rotacao: QUATERNION_IDENTIDADE,
      meiaExtensao: vetor(p.raioCabeca, p.raioCabeca, p.raioCabeca),
    },
    {
      // APOIADAS no assento: `alturaAssento` é o topo da almofada, e a coxa
      // repousa sobre ela
      id: 'coxas',
      forma: 'cuboide',
      centro: vetor(
        colocacao.posicaoPelve.x,
        pessoa.ancora.alturaAssento + p.raioMembro,
        colocacao.posicaoPelve.z + p.comprimentoCoxa / 2,
      ),
      rotacao: QUATERNION_IDENTIDADE,
      meiaExtensao: vetor(p.larguraPelve / 2, p.raioMembro, p.comprimentoCoxa / 2),
    },
  ]
}

interface SegmentoFisicoInicial {
  centro: Vetor3
  inicio: Vetor3
  fim: Vetor3
  comprimento: number
  raio: number
  rotacao: Quaternion
  massaFracao: number
}

const MEMBROS_APRISIONAVEIS: readonly MembroAprisionavel[] = [
  'bracoEsquerdo',
  'bracoDireito',
  'antebracoEsquerdo',
  'antebracoDireito',
  'coxaEsquerda',
  'coxaDireita',
  'pernaEsquerda',
  'pernaDireita',
  'peEsquerdo',
  'peDireito',
]

const NOME_MEMBRO: Record<MembroAprisionavel, string> = {
  bracoEsquerdo: 'braço esquerdo',
  bracoDireito: 'braço direito',
  antebracoEsquerdo: 'antebraço esquerdo',
  antebracoDireito: 'antebraço direito',
  coxaEsquerda: 'coxa esquerda',
  coxaDireita: 'coxa direita',
  pernaEsquerda: 'perna esquerda',
  pernaDireita: 'perna direita',
  peEsquerdo: 'pé esquerdo',
  peDireito: 'pé direito',
}

const ehMembroAprisionavel = (segmento: SegmentoRagdoll): segmento is MembroAprisionavel =>
  MEMBROS_APRISIONAVEIS.includes(segmento as MembroAprisionavel)

function deslocamentoSegmento(
  comprimento: number,
  anguloX: number,
  anguloZ = 0,
): Vetor3 {
  const cosZ = Math.cos(anguloZ)
  return vetor(
    Math.sin(anguloZ) * comprimento,
    -cosZ * Math.cos(anguloX) * comprimento,
    -cosZ * Math.sin(anguloX) * comprimento,
  )
}

function meio(a: Vetor3, b: Vetor3): Vetor3 {
  return vetor((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2)
}

function aplicarQuaternion(v: Vetor3, q: Quaternion): Vetor3 {
  const ix = q.w * v.x + q.y * v.z - q.z * v.y
  const iy = q.w * v.y + q.z * v.x - q.x * v.z
  const iz = q.w * v.z + q.x * v.y - q.y * v.x
  const iw = -q.x * v.x - q.y * v.y - q.z * v.z
  return vetor(
    ix * q.w + iw * -q.x + iy * -q.z - iz * -q.y,
    iy * q.w + iw * -q.y + iz * -q.x - ix * -q.z,
    iz * q.w + iw * -q.z + ix * -q.y - iy * -q.x,
  )
}

function paraLocalDoCorpo(ponto: Vetor3, centro: Vetor3, rotacao: Quaternion): Vetor3 {
  const inversa = { x: -rotacao.x, y: -rotacao.y, z: -rotacao.z, w: rotacao.w }
  return aplicarQuaternion(subtrair(ponto, centro), inversa)
}

function segmentosIniciaisDaPessoa(
  perfil: PerfilVeiculo,
  pessoa: PessoaParaResolver,
): Record<MembroAprisionavel, SegmentoFisicoInicial> {
  const p = pessoa.proporcoes
  const yaw = pessoa.ancora.rotacao.y
  const paraVeiculo = (local: Vetor3) =>
    somar(pessoa.ancora.posicao, girarEmTorno(local, vetor(0, 1, 0), yaw))
  const criar = (
    inicio: Vetor3,
    fim: Vetor3,
    raio: number,
    massaFracao: number,
  ): SegmentoFisicoInicial => {
    const comprimento = comprimento3(subtrair(fim, inicio))
    return {
      inicio,
      fim,
      centro: meio(inicio, fim),
      comprimento,
      raio,
      rotacao: quaternionDeYPara(subtrair(fim, inicio)),
      massaFracao,
    }
  }

  const saida = {} as Record<MembroAprisionavel, SegmentoFisicoInicial>
  const raioCoxa = p.raioMembro * 1.15
  for (const [lado, sufixo] of [[-1, 'Esquerdo'], [1, 'Direito']] as const) {
    const ombro = paraVeiculo(vetor(
      lado * p.larguraOmbros / 2,
      p.alturaPelve / 2 + p.alturaTronco - 0.06,
      0,
    ))
    const cotovelo = somar(
      ombro,
      girarEmTorno(
        deslocamentoSegmento(p.comprimentoBraco, POSE_SENTADA.ombroRad, lado * 0.18),
        vetor(0, 1, 0),
        yaw,
      ),
    )
    const mao = somar(
      cotovelo,
      girarEmTorno(
        deslocamentoSegmento(
          p.comprimentoAntebraco,
          POSE_SENTADA.ombroRad + POSE_SENTADA.cotoveloRad,
          lado * 0.12,
        ),
        vetor(0, 1, 0),
        yaw,
      ),
    )
    const quadril = paraVeiculo(vetor(
      lado * p.larguraPelve * 0.28,
      -p.alturaPelve * 0.1,
      p.profundidadeTronco * 0.1,
    ))
    const joelho = somar(
      quadril,
      girarEmTorno(
        deslocamentoSegmento(p.comprimentoCoxa, POSE_SENTADA.quadrilRad, lado * POSE_SENTADA.abducaoCoxaRad),
        vetor(0, 1, 0),
        yaw,
      ),
    )
    // A escala vertical da maquete é menor que uma perna humana inteira.
    // Em vez de atravessar o assoalho, a canela avança pelo espaço dos pés e
    // termina apoiada logo acima do piso, alcançando a pedaleira.
    const yTornozelo = perfil.pisoY + p.raioMembro * 1.05
    const queda = Math.max(0, joelho.y - yTornozelo)
    const avanco = Math.sqrt(Math.max(p.comprimentoPerna ** 2 - queda ** 2, 0.02))
    const tornozelo = vetor(
      joelho.x,
      yTornozelo,
      joelho.z + avanco,
    )
    const pontaPe = somar(
      tornozelo,
      girarEmTorno(vetor(0, -0.025, Math.max(p.raioMembro * 3, 0.17)), vetor(0, 1, 0), yaw),
    )

    const ladoNome = sufixo === 'Esquerdo' ? 'Esquerdo' : 'Direito'
    const ladoFem = sufixo === 'Esquerdo' ? 'Esquerda' : 'Direita'
    saida[`braco${ladoNome}`] = criar(ombro, cotovelo, p.raioMembro * 0.85, 0.025)
    saida[`antebraco${ladoNome}`] = criar(cotovelo, mao, p.raioMembro * 0.72, 0.015)
    saida[`coxa${ladoFem}`] = criar(quadril, joelho, raioCoxa, 0.055)
    saida[`perna${ladoFem}`] = criar(joelho, tornozelo, p.raioMembro * 0.92, 0.025)
    saida[`pe${ladoNome}`] = criar(tornozelo, pontaPe, p.raioMembro * 0.82, 0.01)
  }
  return saida
}

/** Nome de cada volume do corpo, na ordem em que `volumesDaPessoa` os devolve. */
const NOMES_VOLUME = ['pelve', 'tronco', 'cabeça', 'coxas'] as const

/** Um vínculo de cinto vivo, com o que é preciso para encurtá-lo depois. */
interface VinculoCinto {
  id: string
  junta: ReturnType<typeof RapierNS.World.prototype.createImpulseJoint>
  /** segmento efetivamente retido por esta faixa */
  corpo: RapierNS.RigidBody
  ancoraVeiculo: Vetor3
  ancoraCorpo: Vetor3
  comprimento: number
}

/**
 * Cria as cordas do conjunto de retenção desta posição de ocupante.
 *
 * CORDA, não mola: uma corda impede passar de um comprimento e deixa a folga
 * livre abaixo dele, que é exatamente o comportamento de uma fita de cinto — o
 * corpo anda solto no começo e é detido quando a fita estica.
 *
 * As âncoras saem de `pontosDoCinto()`, a mesma geometria que a cena desenha, e
 * o TIPO do cinto deixa de ser rótulo: três pontos prende ombro e quadril;
 * subabdominal prende só o quadril, e o tronco dobra por cima dele.
 */
function criarCintos(
  R: typeof RapierNS,
  world: RapierNS.World,
  perfil: PerfilVeiculo,
  veiculo: RapierNS.RigidBody,
  corpos: { pelve: RapierNS.RigidBody; tronco: RapierNS.RigidBody },
  origens: { pelve: Vetor3; tronco: Vetor3 },
  pessoa: PessoaParaResolver,
): VinculoCinto[] {
  if (pessoa.retencao === 'semCinto') return []
  const config = retencaoDaAncora(perfil, pessoa.ancora.id)
  if (!config) return []

  const pontos = pontosDoCinto(perfil, pessoa.ancora, config)
  const faixas: Array<{
    id: string
    segmento: 'pelve' | 'tronco'
    noVeiculo: Vetor3
    noCorpo: Vetor3
    folga: number
  }> = []

  if (pontos.ancoragemSuperior && pontos.ombro) {
    faixas.push({
      id: 'diagonal',
      segmento: 'tronco',
      noVeiculo: pontos.ancoragemSuperior,
      noCorpo: pontos.ombro,
      folga: FOLGA_CINTO_M * FRACAO_FOLGA_DIAGONAL,
    })
  }
  faixas.push({
    id: 'pelvicaExterna',
    segmento: 'pelve',
    noVeiculo: pontos.ancoragemInferiorExterna,
    noCorpo: pontos.quadrilExterno,
    folga: FOLGA_CINTO_M,
  })
  faixas.push({
    id: 'pelvicaInterna',
    segmento: 'pelve',
    noVeiculo: pontos.ancoragemInferiorInterna,
    noCorpo: pontos.fecho,
    folga: FOLGA_CINTO_M,
  })

  return faixas.map((faixa) => {
    const corpo = corpos[faixa.segmento]
    const ancoraCorpo = subtrair(faixa.noCorpo, origens[faixa.segmento])
    const comprimento = comprimento3(subtrair(faixa.noVeiculo, faixa.noCorpo)) + faixa.folga
    const junta = world.createImpulseJoint(
      R.JointData.rope(comprimento, paraVetorRapier(faixa.noVeiculo), paraVetorRapier(ancoraCorpo)),
      veiculo,
      corpo,
      true,
    )
    return { id: faixa.id, junta, corpo, ancoraVeiculo: faixa.noVeiculo, ancoraCorpo, comprimento }
  })
}

export function resolverCinematica(
  R: typeof RapierNS,
  entrada: EntradaSolucionador,
): ResultadoFisica {
  const { perfil, estados, tipoColisao, cargas, pessoas } = entrada
  const tonus = entrada.tonus ?? 'alerta'
  const cfgTonus = CONFIG_TONUS[tonus]
  const alvoImpacto = entrada.alvoImpacto ?? 'outroVeiculo'
  const passos = entrada.passos ?? PASSOS_PADRAO
  const velocidadeKmh = velocidadeDoEvento(tipoColisao, entrada.velocidadeImpactoKmh)
  const duracaoS = duracaoRealS(tipoColisao, velocidadeKmh)
  const passoS = duracaoS / passos

  const quadroEm = (t: number) => amostrarCinematica(tipoColisao, t, velocidadeKmh)
  const colisaoEntreVeiculos = entrada.colisaoEntreVeiculos ?? null
  const extraEm = (t: number) => ajustePosImpactoA(colisaoEntreVeiculos, t).deslocamento
  const mundoEm = (t: number, ponto: Vetor3) => somar(paraMundo(perfil, quadroEm(t), ponto), extraEm(t))

  const world = new R.World({ x: 0, y: -GRAVIDADE, z: 0 })
  world.timestep = passoS

  // Grupos disjuntos: o habitáculo só interage com ocupantes/carga e a casca
  // externa só com o alvo externo. O grupo padrão do Rapier interage com todos.
  const GRUPO_INTERNO = 0x00010001
  const GRUPO_EXTERNO = 0x00020002

  // ---- veículo: um corpo cinemático com a casca e os móveis como colisores
  const veiculo = world.createRigidBody(R.RigidBodyDesc.kinematicPositionBased())
  const nomePorColisor = new Map<number, string>()
  for (const colisor of colisoresDoHabitaculo(perfil, estados)) {
    const desc = descreverColisor(R, colisor)
    desc.setFriction(ATRITO_INTERIOR)
    desc.setRestitution(RESTITUICAO_INTERIOR)
    desc.setCollisionGroups(GRUPO_INTERNO)
    const criado = world.createCollider(desc, veiculo)
    nomePorColisor.set(criado.handle, colisor.nome)
  }

  // ---- impacto externo: separado dos colisores do habitáculo
  //
  // O exterior usa um grupo de colisão próprio. Assim o segundo veículo, poste
  // ou muro interage com a casca externa sem virar uma parede invisível para os
  // ocupantes dentro da cabine.
  const veiculoExterior = world.createRigidBody(R.RigidBodyDesc.kinematicPositionBased())
  world.createCollider(
    R.ColliderDesc.cuboid(perfil.largura / 2, perfil.tetoY / 2, perfil.comprimento / 2)
      .setTranslation(0, perfil.tetoY / 2, 0)
      .setCollisionGroups(GRUPO_EXTERNO),
    veiculoExterior,
  )

  const definicaoImpacto = colisao(tipoColisao)

  /**
   * Cria o corpo de UM contato externo (outro veículo, poste ou muro).
   *
   * Extraída para servir tanto o contato principal quanto, no engavetamento
   * (`frontalComTraseira`), o segundo — a mesma conta de posicionar o alvo
   * para chegar exatamente no instante de contato, só que para um instante e
   * uma direção diferentes. Nenhum número muda para o contato principal.
   */
  const criarAlvoExterno = (
    direcaoImpacto: Vetor3,
    alvo: TipoAlvoImpacto,
    tContatoNorm: number,
  ): RapierNS.RigidBody => {
    const longitudinal = Math.abs(direcaoImpacto.z) >= Math.abs(direcaoImpacto.x)
    const ladoFrente = direcaoImpacto.z < 0
    // a força aponta para +X ⇒ o contato é na face −X (passageiro), e vice-versa
    const contatoEmMenosX = direcaoImpacto.x > 0

    const duracaoReal = duracaoRealS(tipoColisao, velocidadeKmh)
    const tContatoReal = tContatoNorm * duracaoReal
    const posVeiculoContato = mundoEm(tContatoNorm, vetor(0, 0, 0))

    if (alvo === 'outroVeiculo') {
      const velocidadeAlvo = (velocidadeKmh / 3.6) * 0.55
      const rotacaoY = longitudinal ? (ladoFrente ? Math.PI : 0) : contatoEmMenosX ? Math.PI / 2 : -Math.PI / 2

      let inicioX = 0
      let inicioZ = 0

      if (longitudinal) {
        if (ladoFrente) {
          // Contato frontal: no instante de contato o alvo deve estar em posVeiculoContato.z + perfil.comprimento
          const zAlvoContato = posVeiculoContato.z + perfil.comprimento
          inicioZ = zAlvoContato + velocidadeAlvo * tContatoReal
          inicioX = 0
        } else {
          // Contato traseiro
          const zAlvoContato = posVeiculoContato.z - perfil.comprimento
          inicioZ = zAlvoContato - velocidadeAlvo * tContatoReal
          inicioX = 0
        }
      } else {
        if (contatoEmMenosX) {
          // Contato na face −X (lado do passageiro)
          const xAlvoContato = posVeiculoContato.x - perfil.largura / 2 - perfil.comprimento / 2
          inicioX = xAlvoContato - velocidadeAlvo * tContatoReal
          inicioZ = posVeiculoContato.z
        } else {
          const xAlvoContato = posVeiculoContato.x + perfil.largura / 2 + perfil.comprimento / 2
          inicioX = xAlvoContato + velocidadeAlvo * tContatoReal
          inicioZ = posVeiculoContato.z
        }
      }

      const corpo = world.createRigidBody(
        R.RigidBodyDesc.dynamic()
          .setTranslation(inicioX, 0, inicioZ)
          .setRotation(quaternionEmY(rotacaoY))
          .setLinvel(
            direcaoImpacto.x * velocidadeAlvo,
            0,
            direcaoImpacto.z * velocidadeAlvo,
          )
          .setLinearDamping(0.28)
          .setAngularDamping(1.8)
          .setCcdEnabled(true),
      )
      world.createCollider(
        R.ColliderDesc.cuboid(perfil.largura / 2, perfil.tetoY / 2, perfil.comprimento / 2)
          .setTranslation(0, perfil.tetoY / 2, 0)
          .setMass(1300)
          .setFriction(0.75)
          .setRestitution(0.08)
          .setCollisionGroups(GRUPO_EXTERNO),
        corpo,
      )
      return corpo
    }

    let faceX = 0
    let faceZ = 0

    if (longitudinal) {
      const offset = alvo === 'poste' ? 0.19 : 0.11
      faceZ = ladoFrente
        ? posVeiculoContato.z + perfil.comprimento / 2 + offset
        : posVeiculoContato.z - perfil.comprimento / 2 - offset
      faceX = 0
    } else {
      const offset = alvo === 'poste' ? 0.19 : 0.11
      faceX = contatoEmMenosX
        ? posVeiculoContato.x - perfil.largura / 2 - offset
        : posVeiculoContato.x + perfil.largura / 2 + offset
      faceZ = posVeiculoContato.z
    }

    const corpo = world.createRigidBody(R.RigidBodyDesc.fixed().setTranslation(faceX, 0, faceZ))
    const forma =
      alvo === 'poste'
        ? R.ColliderDesc.cylinder(1.55, 0.19).setTranslation(0, 1.55, 0)
        : longitudinal
          ? R.ColliderDesc.cuboid(2.9, 1.2, 0.11).setTranslation(0, 1.2, 0)
          : R.ColliderDesc.cuboid(0.11, 1.2, 2.9).setTranslation(0, 1.2, 0)
    world.createCollider(forma.setCollisionGroups(GRUPO_EXTERNO), corpo)
    return corpo
  }

  /**
   * Todos os contatos externos do evento, na ordem em que acontecem. Um só na
   * esmagadora maioria dos tipos; dois no engavetamento.
   */
  const alvosExternos: Array<{ corpo: RapierNS.RigidBody; quadros: QuadroCorpo[] }> = []

  if (definicaoImpacto.direcaoImpacto) {
    const corpo = criarAlvoExterno(
      definicaoImpacto.direcaoImpacto,
      alvoImpacto,
      instanteDoContato(tipoColisao),
    )
    alvosExternos.push({ corpo, quadros: [] })
  }

  const impactoSecundario = definicaoImpacto.impactoSecundario
  if (impactoSecundario) {
    const tSecundario = instantesDeContato(tipoColisao)[1] ?? instanteDoContato(tipoColisao)
    const corpo = criarAlvoExterno(impactoSecundario.direcaoImpacto, impactoSecundario.alvo, tSecundario)
    alvosExternos.push({ corpo, quadros: [] })
  }

  if (alvosExternos.length > 0) {
    // Piso exclusivo da interação externa, para o(s) segundo(s) veículo(s)
    // apoiar(em) e continuar(em) reagindo depois do contato.
    const pisoExterno = world.createRigidBody(R.RigidBodyDesc.fixed())
    world.createCollider(
      R.ColliderDesc.cuboid(12, 0.04, 12)
        .setTranslation(0, -0.04, 0)
        .setFriction(0.9)
        .setCollisionGroups(GRUPO_EXTERNO),
      pisoExterno,
    )
  }

  /**
   * Velocidade de mundo de um ponto do veículo, no instante.
   *
   * ANALÍTICA, e a mesma função para as duas perguntas que o solver faz — com que
   * velocidade o corpo nasce, e contra que velocidade a dele é medida depois.
   * Antes eram duas diferenças finitas independentes sobre um caminho que dava
   * saltos de até 22 km/h entre passos; a diferença finita registrava o salto
   * como desaceleração, e o painel reportava impacto onde não houve nenhum.
   */
  const velocidadeVeiculoEm = (t: number, ponto: Vetor3): Vetor3 =>
    somar(velocidadeNoPonto(perfil, tipoColisao, t, ponto, velocidadeKmh), velocidadeAjusteA(colisaoEntreVeiculos, t))

  const velocidadeInicialEm = (ponto: Vetor3): Vetor3 => velocidadeVeiculoEm(0, ponto)

  // ---- ocupantes: ragdoll DINÂMICO completo
  //
  // Treze corpos: pelve, tórax, cabeça e pares de braços, antebraços, coxas,
  // pernas e pés. A extração continua usando o rig autoral determinístico.
  const corposPessoa = pessoas.map((pessoa) => {
    const origemPelve = pessoa.ancora.posicao
    const volumes = volumesDaPessoa(pessoa, vetor(0, 0, 0), 0)
    const volumePelve = volumes.find((v) => v.id === 'pelve')!
    const volumeTronco = volumes.find((v) => v.id === 'tronco')!
    const volumeCabeca = volumes.find((v) => v.id === 'cabeca')!
    const membros = segmentosIniciaisDaPessoa(perfil, pessoa)

    const criarCorpo = (origem: Vetor3, rotacao: Quaternion = QUATERNION_IDENTIDADE) => {
      const mundoInicial = mundoEm(0, origem)
      const velocidade = velocidadeInicialEm(origem)
      return world.createRigidBody(
        R.RigidBodyDesc.dynamic()
          .setTranslation(mundoInicial.x, mundoInicial.y, mundoInicial.z)
          .setRotation(rotacao)
          .setLinvel(velocidade.x, velocidade.y, velocidade.z)
          .setLinearDamping(ATENUACAO_LINEAR_CORPO)
          .setAngularDamping(ATENUACAO_ANGULAR_CORPO)
          // sem atenuação, um corpo parado fica parado, e a traseira começa com
          // veículo e ocupante ambos em repouso: deixar o Rapier adormecê-lo
          // seria trocar determinismo por heurística justamente no tipo em que o
          // evento parte do zero
          .setCanSleep(false)
          .setCcdEnabled(true),
      )
    }

    const pelve = criarCorpo(origemPelve)
    const tronco = criarCorpo(volumeTronco.centro)
    const cabeca = criarCorpo(volumeCabeca.centro)

    const criarColisor = (
      corpo: RapierNS.RigidBody,
      volume: (typeof volumes)[number],
      origemCorpo: Vetor3,
      massaKg: number,
    ) => {
      const desc =
        volume.forma === 'esfera'
          ? R.ColliderDesc.ball(volume.meiaExtensao.x)
          : R.ColliderDesc.cuboid(
              volume.meiaExtensao.x,
              volume.meiaExtensao.y,
              volume.meiaExtensao.z,
            )
      const centro = subtrair(volume.centro, origemCorpo)
      desc.setTranslation(centro.x, centro.y, centro.z)
      desc.setRotation(volume.rotacao)
      desc.setMass(massaKg)
      desc.setFriction(ATRITO_CORPO)
      desc.setRestitution(RESTITUICAO_CORPO)
      desc.setCollisionGroups(GRUPO_INTERNO)
      return world.createCollider(desc, corpo)
    }

    const massa = pessoa.proporcoes.massaKg
    const colisorPelve = criarColisor(pelve, volumePelve, origemPelve, massa * 0.32)
    const colisorTronco = criarColisor(tronco, volumeTronco, volumeTronco.centro, massa * 0.42)
    const colisorCabeca = criarColisor(cabeca, volumeCabeca, volumeCabeca.centro, massa * 0.08)

    const corposMembros = {} as Record<MembroAprisionavel, RapierNS.RigidBody>
    const colisoresMembros = {} as Record<MembroAprisionavel, RapierNS.Collider>
    for (const segmento of MEMBROS_APRISIONAVEIS) {
      const definicao = membros[segmento]
      const corpo = criarCorpo(definicao.centro, definicao.rotacao)
      const meiaParteReta = Math.max(definicao.comprimento / 2 - definicao.raio, 0.01)
      const desc = R.ColliderDesc.capsule(meiaParteReta, definicao.raio)
        .setMass(massa * definicao.massaFracao)
        .setFriction(ATRITO_CORPO)
        .setRestitution(RESTITUICAO_CORPO)
        .setCollisionGroups(GRUPO_INTERNO)
      const colisor = world.createCollider(desc, corpo)
      corposMembros[segmento] = corpo
      colisoresMembros[segmento] = colisor
      nomePorColisor.set(colisor.handle, `${pessoa.ancora.nome} — ${NOME_MEMBRO[segmento]}`)
    }

    nomePorColisor.set(colisorPelve.handle, `${pessoa.ancora.nome} — pelve`)
    nomePorColisor.set(colisorTronco.handle, `${pessoa.ancora.nome} — tronco`)
    nomePorColisor.set(colisorCabeca.handle, `${pessoa.ancora.nome} — cabeça`)

    /**
     * Junta esférica do ragdoll, SEM contato entre os dois segmentos ligados.
     *
     * É a correção do defeito que o instrutor viu como "ele já machuca assim que
     * começa a se deslocar". Cada segmento nasce encaixado dentro do pai — a coxa
     * começa no quadril, que fica dentro do cuboide da pelve; o braço começa no
     * ombro, dentro do tronco —, e o Rapier resolvia essa sobreposição inicial
     * como penetração a expulsar. Medido no primeiro passo da frontal: pés a 754
     * e 641 km/h em relação ao carro, coxas a 317, pelve a 220. Nada disso vinha
     * do acidente; vinha do nascimento do boneco. O que sobrava abaixo do teto de
     * plausibilidade (75 km/h) virava impacto reportado contra o para-brisa, o
     * volante e a pedaleira nos primeiros 2% do evento.
     *
     * Desligar o contato ENTRE segmentos articulados é o que se faz em qualquer
     * ragdoll, e não custa nada à aula: o corpo continua colidindo com o
     * habitáculo, com a carga solta e com os outros ocupantes, que é o que a aba
     * mede. As cordas do cinto seguem com contato ligado — elas ancoram no
     * veículo, e desligá-lo faria o ocupante retido atravessar o assoalho.
     */
    const articularEsfericaComMotor = (
      ancoraPai: Vetor3,
      ancoraFilho: Vetor3,
      pai: RapierNS.RigidBody,
      filho: RapierNS.RigidBody,
      stiffness: number,
      damping: number,
      maxTorque: number,
      contatoEntreSegmentos = false,
    ) => {
      const juntaBruta = world.createImpulseJoint(
        R.JointData.spherical(paraVetorRapier(ancoraPai), paraVetorRapier(ancoraFilho)),
        pai,
        filho,
        true,
      )
      juntaBruta.setContactsEnabled(contatoEntreSegmentos)
      const junta = new R.SphericalImpulseJoint(
        (juntaBruta as any).rawSet,
        (juntaBruta as any).bodySet,
        juntaBruta.handle,
      )

      for (const axis of [R.JointAxis.AngX, R.JointAxis.AngY, R.JointAxis.AngZ]) {
        junta.configureMotorPosition(axis, 0, stiffness, damping)
        junta.setMotorMaxForce(axis, maxTorque)
      }
      return junta
    }

    const eixo = eixoColunaDaVitima(
      { posicaoPelve: origemPelve, guinadaRad: pessoa.ancora.rotacao.y, reclinacaoRad: 0 },
      pessoa.proporcoes,
      POSE_SENTADA,
    )

    articularEsfericaComMotor(
      subtrair(eixo.baseColuna, origemPelve),
      subtrair(eixo.baseColuna, volumeTronco.centro),
      pelve,
      tronco,
      cfgTonus.stiffnessColuna,
      cfgTonus.dampingColuna,
      cfgTonus.maxTorqueCervical * 1.5,
    )
    articularEsfericaComMotor(
      subtrair(eixo.topoColuna, volumeTronco.centro),
      subtrair(eixo.topoColuna, volumeCabeca.centro),
      tronco,
      cabeca,
      cfgTonus.stiffnessPescoco,
      cfgTonus.dampingPescoco,
      cfgTonus.maxTorqueCervical,
      // a junta esférica não tem limite de ângulo: é o queixo encostando no
      // peito que para a flexão, como no corpo real. Sentados, cabeça e tórax
      // ficam a ~9 cm, então o contato não dispara nada no assentamento.
      true,
    )

    const ligar = (
      pai: RapierNS.RigidBody,
      centroPai: Vetor3,
      rotacaoPai: Quaternion,
      filho: RapierNS.RigidBody,
      definicaoFilho: SegmentoFisicoInicial,
      ponto: Vetor3,
      stiffnessMult = 1.0,
      maxTorqueMult = 1.0,
    ) => {
      return articularEsfericaComMotor(
        paraLocalDoCorpo(ponto, centroPai, rotacaoPai),
        paraLocalDoCorpo(ponto, definicaoFilho.centro, definicaoFilho.rotacao),
        pai,
        filho,
        cfgTonus.stiffnessMembros * stiffnessMult,
        cfgTonus.dampingMembros * stiffnessMult,
        cfgTonus.maxTorqueMembros * maxTorqueMult,
      )
    }

    for (const lado of ['Esquerdo', 'Direito'] as const) {
      const fem = lado === 'Esquerdo' ? 'Esquerda' : 'Direita'
      const braco = `braco${lado}` as const
      const antebraco = `antebraco${lado}` as const
      const coxa = `coxa${fem}` as const
      const perna = `perna${fem}` as const
      const pe = `pe${lado}` as const

      ligar(
        tronco,
        volumeTronco.centro,
        QUATERNION_IDENTIDADE,
        corposMembros[braco],
        membros[braco],
        membros[braco].inicio,
        1.0,
        1.0,
      )
      ligar(
        corposMembros[braco],
        membros[braco].centro,
        membros[braco].rotacao,
        corposMembros[antebraco],
        membros[antebraco],
        membros[braco].fim,
        1.2,
        1.2,
      )
      ligar(
        pelve,
        origemPelve,
        QUATERNION_IDENTIDADE,
        corposMembros[coxa],
        membros[coxa],
        membros[coxa].inicio,
        1.2,
        1.2,
      )
      ligar(
        corposMembros[coxa],
        membros[coxa].centro,
        membros[coxa].rotacao,
        corposMembros[perna],
        membros[perna],
        membros[coxa].fim,
        1.4,
        1.3,
      )
      ligar(
        corposMembros[perna],
        membros[perna].centro,
        membros[perna].rotacao,
        corposMembros[pe],
        membros[pe],
        membros[perna].fim,
        1.0,
        1.0,
      )
    }

    const cintos = criarCintos(
      R,
      world,
      perfil,
      veiculo,
      { pelve, tronco },
      { pelve: origemPelve, tronco: volumeTronco.centro },
      pessoa,
    )

    const origensMembros = Object.fromEntries(
      MEMBROS_APRISIONAVEIS.map((segmento) => [segmento, membros[segmento].centro]),
    ) as Record<MembroAprisionavel, Vetor3>
    const listasColisoresMembros = Object.fromEntries(
      MEMBROS_APRISIONAVEIS.map((segmento) => [segmento, [colisoresMembros[segmento]]]),
    ) as Record<MembroAprisionavel, RapierNS.Collider[]>

    return {
      pessoa,
      corpo: pelve,
      segmentos: { pelve, tronco, cabeca, ...corposMembros },
      origens: {
        pelve: origemPelve,
        tronco: volumeTronco.centro,
        cabeca: volumeCabeca.centro,
        ...origensMembros,
      },
      colisores: {
        pelve: [colisorPelve],
        tronco: [colisorTronco],
        cabeca: [colisorCabeca],
        ...listasColisoresMembros,
      },
      cintos,
      origemPelve,
      definicoesMembros: membros,
    }
  })

  // ---- carga solta: corpos dinâmicos, com a massa e o volume do catálogo
  const corposCarga = cargas.map((carga) => {
    const mundoInicial = mundoEm(0, carga.origem)
    // a carga entra no acidente com a velocidade que o veículo tinha: é o
    // princípio da aula, e vem da mesma fonte analítica que o ocupante usa
    const velocidade = velocidadeInicialEm(carga.origem)
    const corpo = world.createRigidBody(
      R.RigidBodyDesc.dynamic()
        .setTranslation(mundoInicial.x, mundoInicial.y, mundoInicial.z)
        .setLinvel(velocidade.x, velocidade.y, velocidade.z)
        .setCcdEnabled(true),
    )
    const d = carga.corpo.dimensoes
    const desc = R.ColliderDesc.cuboid(d.x / 2, d.y / 2, d.z / 2)
    desc.setMass(carga.corpo.massaKg)
    desc.setFriction(ATRITO_CARGA)
    desc.setRestitution(RESTITUICAO_CARGA)
    desc.setCollisionGroups(GRUPO_INTERNO)
    const colisor = world.createCollider(desc, corpo)
    return { carga, corpo, colisor, velocidadeInicial: velocidade }
  })

  /**
   * Todo corpo dinâmico da cena, na mesma forma.
   *
   * Carga e ocupante passam pela MESMA detecção de impacto: os dois têm massa,
   * volume e velocidade relativa ao veículo, e a pergunta "em que bateu e com
   * que força" é a mesma. `referencia` é o ponto do veículo contra o qual a
   * velocidade relativa é medida.
   */
  const corposDinamicos = [
    ...corposCarga.map((c) => ({
      id: c.carga.id,
      nome: c.carga.corpo.nome,
      massaKg: c.carga.corpo.massaKg,
      corpo: c.corpo,
      colisores: [c.colisor],
      referencia: c.carga.origem,
      ignorarPrefixo: null as string | null,
      idOcupante: null as string | null,
      segmento: null as SegmentoRagdoll | null,
    })),
    ...corposPessoa.flatMap((p) => {
      const massa = p.pessoa.proporcoes.massaKg
      const prefixo = `${p.pessoa.ancora.nome} —`
      const nucleo = [
        {
          id: idSegmentoRagdoll(p.pessoa.id, 'pelve'),
          nome: `${p.pessoa.ancora.nome} — pelve`,
          massaKg: massa * 0.32,
          corpo: p.segmentos.pelve,
          colisores: p.colisores.pelve,
          referencia: p.origens.pelve,
          ignorarPrefixo: prefixo,
          idOcupante: p.pessoa.id,
          segmento: 'pelve' as SegmentoRagdoll,
        },
        {
          id: idSegmentoRagdoll(p.pessoa.id, 'tronco'),
          nome: `${p.pessoa.ancora.nome} — tronco`,
          massaKg: massa * 0.42,
          corpo: p.segmentos.tronco,
          colisores: p.colisores.tronco,
          referencia: p.origens.tronco,
          ignorarPrefixo: prefixo,
          idOcupante: p.pessoa.id,
          segmento: 'tronco' as SegmentoRagdoll,
        },
        {
          id: idSegmentoRagdoll(p.pessoa.id, 'cabeca'),
          nome: `${p.pessoa.ancora.nome} — cabeça`,
          massaKg: massa * 0.08,
          corpo: p.segmentos.cabeca,
          colisores: p.colisores.cabeca,
          referencia: p.origens.cabeca,
          ignorarPrefixo: prefixo,
          idOcupante: p.pessoa.id,
          segmento: 'cabeca' as SegmentoRagdoll,
        },
      ]
      const membros = MEMBROS_APRISIONAVEIS.map((segmento) => ({
        id: idSegmentoRagdoll(p.pessoa.id, segmento),
        nome: `${p.pessoa.ancora.nome} — ${NOME_MEMBRO[segmento]}`,
        massaKg: massa * p.definicoesMembros[segmento].massaFracao,
        corpo: p.segmentos[segmento],
        colisores: p.colisores[segmento],
        referencia: p.origens[segmento],
        ignorarPrefixo: prefixo,
        idOcupante: p.pessoa.id,
        segmento: segmento as SegmentoRagdoll,
      }))
      return [...nucleo, ...membros]
    }),
  ]

  const trajetorias = new Map<string, QuadroCorpo[]>(
    corposDinamicos.map((corpo) => [corpo.id, []] as [string, QuadroCorpo[]]),
  )
  const aprisionamentos = new Map<string, AprisionamentoMembro>()
  const contatoSustentado = new Map<string, { assinatura: string; passos: number }>()
  const ejecoes: EjecaoOcupante[] = []
  const ejetados = new Set<string>()
  const estresseBiomecanicoPorPessoa = new Map<string, LeituraBiomecanicaInstante[]>(
    pessoas.map((p) => [p.id, []]),
  )
  /** Maior desaceleração já vista por carga: é o candidato a impacto. */
  const melhorImpacto = new Map<string, ImpactoCarga & { quedaKmh: number }>()
  /** Histórico de velocidade relativa por carga, para medir a queda na janela. */
  const historico = new Map<string, number[]>(corposDinamicos.map((c) => [c.id, []]))
  const passosDaJanela = Math.max(1, Math.round(JANELA_IMPACTO_S / passoS))

  /** Velocidade do veículo no instante, medida no próprio ponto do corpo. */
  const velocidadeDoVeiculo = velocidadeVeiculoEm

  /**
   * Coloca o veículo onde a timeline diz.
   *
   * Só ele é cinemático. O corpo do ocupante é dinâmico: quem decide para onde
   * ele vai é a engine, sob o vínculo do cinto.
   *
   * A POSE do ocupante não entra aqui. Ela continua vindo dos quadros-chave e é
   * aplicada no desenho (`CenaCinematica`), onde a lição vive — na colisão
   * traseira, a cabeça atrasando em relação ao tronco. Tentar reproduzi-la
   * também nos colisores, recolocando-os a cada passo, empurrava o corpo para
   * cima: o tronco tombando mergulhava na almofada e a penetração era resolvida
   * como impulso. O resultado era um ocupante que subia contra o teto, e o teto
   * levava o crédito de batidas que aconteceram no volante. Os colisores ficam
   * na forma sentada neutra, que é aproximação honesta para o que eles fazem —
   * deter o corpo e dizer contra o quê.
   */
  const posicionarVeiculo = (t: number) => {
    const quadro = quadroEm(t)
    // o corpo do veículo É a transformação veículo→mundo: mesma translação e
    // mesma rolagem que `paraMundo` aplica aos pontos
    veiculo.setNextKinematicTranslation(paraVetorRapier(mundoEm(t, vetor(0, 0, 0))))
    veiculo.setNextKinematicRotation(quaternionEmZ(quadro.rotacaoVeiculoRad.z))
    veiculoExterior.setNextKinematicTranslation(paraVetorRapier(mundoEm(t, vetor(0, 0, 0))))
    veiculoExterior.setNextKinematicRotation(quaternionEmZ(quadro.rotacaoVeiculoRad.z))
  }

  /**
   * Recolhe a folga do cinto na janela do pretensionador.
   *
   * `ImpulseJoint` não expõe alteração de comprimento, então a corda é removida e
   * recriada mais curta. Acontece uma vez por ocupante, dentro da janela, e não
   * custa determinismo: o instante é função do passo, não do relógio.
   */
  const pretensionadores = new Set<string>()
  const acionarPretensionadores = (t: number) => {
    if (!colisao(tipoColisao).acionaPretensionador) return
    if (t < TEMPO_PRETENSIONADOR) return
    for (const alvo of corposPessoa) {
      if (pretensionadores.has(alvo.pessoa.id)) continue
      const config = retencaoDaAncora(perfil, alvo.pessoa.ancora.id)
      if (!config?.pretensionador || alvo.cintos.length === 0) continue
      pretensionadores.add(alvo.pessoa.id)

      alvo.cintos = alvo.cintos.map((cinto) => {
        world.removeImpulseJoint(cinto.junta, true)
        const comprimento = Math.max(cinto.comprimento - RECOLHIMENTO_PRETENSIONADOR_M, 0.02)
        return {
          ...cinto,
          comprimento,
          junta: world.createImpulseJoint(
            R.JointData.rope(
              comprimento,
              paraVetorRapier(cinto.ancoraVeiculo),
              paraVetorRapier(cinto.ancoraCorpo),
            ),
            veiculo,
            cinto.corpo,
            true,
          ),
        }
      })
    }
  }

  /**
   * Abre e fecha as bolsas que ESTE evento aciona e o perfil declara — as
   * mesmas que a cena desenha (`pontosSrs`), na mesma posição.
   */
  const definicaoEvento = colisao(tipoColisao)
  const chavesAcionadas = new Set<ChaveSrs>([
    ...definicaoEvento.srsAcionados,
    ...(definicaoEvento.impactoSecundario?.srsAcionados ?? []),
  ])
  const bolsas = modulosSrs(perfil)
    .filter((m) => chavesAcionadas.has(m.chave))
    .map((modulo) => ({ modulo, colisor: null as RapierNS.Collider | null, esvaziou: false }))
  const acionarAirbags = (t: number) => {
    if (bolsas.length === 0) return
    const expansao = amostrarCinematica(tipoColisao, t).expansaoAirbag
    for (const bolsa of bolsas) {
      if (!bolsa.colisor && !bolsa.esvaziou && expansao >= EXPANSAO_AIRBAG_ATIVO) {
        const { centro, dimensoes, inclinacaoX } = bolsa.modulo.bolsa
        const desc = R.ColliderDesc.cuboid(
          (dimensoes[0] / 2) * FATOR_COMPRESSAO_AIRBAG,
          (dimensoes[1] / 2) * FATOR_COMPRESSAO_AIRBAG,
          (dimensoes[2] / 2) * FATOR_COMPRESSAO_AIRBAG,
        )
          .setTranslation(centro.x, centro.y, centro.z)
          .setRotation(quaternionEmX(inclinacaoX))
          .setFriction(ATRITO_AIRBAG)
          .setRestitution(RESTITUICAO_AIRBAG)
          .setCollisionGroups(GRUPO_INTERNO)
        bolsa.colisor = world.createCollider(desc, veiculo)
        nomePorColisor.set(bolsa.colisor.handle, bolsa.modulo.chave)
      } else if (bolsa.colisor && expansao < EXPANSAO_AIRBAG_ESVAZIADO) {
        world.removeCollider(bolsa.colisor, true)
        bolsa.colisor = null
        bolsa.esvaziou = true
      }
    }
  }

  /** Ponto de mundo trazido de volta ao referencial do veículo no instante. */
  const paraVeiculo = (t: number, mundo: Vetor3): Vetor3 =>
    doMundo(perfil, quadroEm(t), subtrair(mundo, extraEm(t)))

  /**
   * Acomoda o boneco no banco antes de gravar o primeiro quadro.
   *
   * Com o veículo cravado no instante zero — mesma posição e mesma rotação a cada
   * passo, logo velocidade cinemática nula — e amortecimento alto nos corpos, as
   * expulsões do nascimento se dissipam em vez de virarem velocidade relativa. O
   * alvo externo é congelado e devolvido ao seu estado depois: ele não está
   * acomodando nada, está a caminho.
   */
  const assentar = () => {
    const zero = { x: 0, y: 0, z: 0 }
    const quadroZero = quadroEm(0)
    // teleporta o veículo para o instante zero: sem isto, o primeiro passo o vê
    // saindo da origem padrão do Rapier e lhe atribui uma velocidade enorme
    for (const corpo of [veiculo, veiculoExterior]) {
      corpo.setTranslation(paraVetorRapier(mundoEm(0, vetor(0, 0, 0))), false)
      corpo.setRotation(quaternionEmZ(quadroZero.rotacaoVeiculoRad.z), false)
    }

    const estadosAlvos = alvosExternos.map(({ corpo }) => ({
      corpo,
      traducao: { ...corpo.translation() },
      rotacao: { ...corpo.rotation() },
      linear: { ...corpo.linvel() },
    }))

    const dinamicos = [
      ...corposPessoa.flatMap((p) => Object.values(p.segmentos)),
      ...corposCarga.map((c) => c.corpo),
    ]
    for (const corpo of dinamicos) {
      corpo.setLinvel(zero, false)
      corpo.setAngvel(zero, false)
      corpo.setLinearDamping(ATENUACAO_ASSENTAMENTO)
      corpo.setAngularDamping(ATENUACAO_ASSENTAMENTO)
    }

    world.timestep = PASSO_ASSENTAMENTO_S
    for (let i = 0; i < PASSOS_ASSENTAMENTO; i += 1) {
      posicionarVeiculo(0)
      for (const estado of estadosAlvos) {
        estado.corpo.setTranslation(estado.traducao, false)
        estado.corpo.setRotation(estado.rotacao, false)
        estado.corpo.setLinvel(zero, false)
        estado.corpo.setAngvel(zero, false)
      }
      world.step()
    }
    world.timestep = passoS

    for (const estado of estadosAlvos) {
      estado.corpo.setTranslation(estado.traducao, true)
      estado.corpo.setRotation(estado.rotacao, true)
      estado.corpo.setLinvel(estado.linear, true)
      estado.corpo.setAngvel(zero, true)
    }

    // agora sim o corpo entra no evento com a velocidade do veículo — medida no
    // ponto em que ele efetivamente ACABOU de se acomodar, não no ponto em que a
    // pose autoral o colocava
    const giro = taxaDeRolagemRadPorS(tipoColisao, 0, velocidadeKmh)
    for (const corpo of dinamicos) {
      corpo.setLinearDamping(ATENUACAO_LINEAR_CORPO)
      corpo.setAngularDamping(ATENUACAO_ANGULAR_CORPO)
      const posicao = corpo.translation()
      const noVeiculo = doMundo(perfil, quadroZero, vetor(posicao.x, posicao.y, posicao.z))
      const velocidade = velocidadeInicialEm(noVeiculo)
      corpo.setLinvel(paraVetorRapier(velocidade), true)
      // e girando junto com a lataria: quem está sentado roda com o carro
      corpo.setAngvel({ x: 0, y: 0, z: giro }, true)
    }
  }

  assentar()
  posicionarVeiculo(0)

  for (let passo = 0; passo <= passos; passo += 1) {
    const t = passo / passos

    for (const alvoExterno of alvosExternos) {
      const p = alvoExterno.corpo.translation()
      const r = alvoExterno.corpo.rotation()
      alvoExterno.quadros.push({
        posicao: vetor(p.x, p.y, p.z),
        rotacao: { x: r.x, y: r.y, z: r.z, w: r.w },
      })
    }

    // ---- ocupantes: trajetória, biomecânica e ejeção medida
    for (const alvo of corposPessoa) {
      const rotTronco = alvo.segmentos.tronco.rotation()
      const rotCabeca = alvo.segmentos.cabeca.rotation()
      const { graus: anguloCervicalGraus } = calcularAnguloCervical(rotTronco, rotCabeca)
      const nivelCervical = avaliarEstresseCervical(anguloCervicalGraus)

      let sobrecargaJoelhos: 'normal' | 'sobrecarga' | 'luxacaoProvavel' = 'normal'
      let sobrecargaCotovelos: 'normal' | 'sobrecarga' | 'luxacaoProvavel' = 'normal'

      for (const lado of ['Esquerda', 'Direita'] as const) {
        const coxa = alvo.segmentos[`coxa${lado}`]
        const perna = alvo.segmentos[`perna${lado}`]
        if (coxa && perna) {
          const ang = calcularAnguloCervical(coxa.rotation(), perna.rotation()).graus
          if (ang > 145) {
            sobrecargaJoelhos = 'luxacaoProvavel'
          } else if (ang > 120 && sobrecargaJoelhos !== 'luxacaoProvavel') {
            sobrecargaJoelhos = 'sobrecarga'
          }
        }
      }

      for (const lado of ['Esquerdo', 'Direito'] as const) {
        const braco = alvo.segmentos[`braco${lado}`]
        const antebraco = alvo.segmentos[`antebraco${lado}`]
        if (braco && antebraco) {
          const ang = calcularAnguloCervical(braco.rotation(), antebraco.rotation()).graus
          if (ang > 150) {
            sobrecargaCotovelos = 'luxacaoProvavel'
          } else if (ang > 125 && sobrecargaCotovelos !== 'luxacaoProvavel') {
            sobrecargaCotovelos = 'sobrecarga'
          }
        }
      }

      estresseBiomecanicoPorPessoa.get(alvo.pessoa.id)?.push({
        anguloCervicalGraus: Math.round(anguloCervicalGraus * 10) / 10,
        nivelCervical,
        estresseJoelhos: sobrecargaJoelhos,
        estresseCotovelos: sobrecargaCotovelos,
      })

      const traducao = alvo.corpo.translation()

      if (ejetados.has(alvo.pessoa.id)) continue
      const noVeiculo = paraVeiculo(t, vetor(traducao.x, traducao.y, traducao.z))
      if (dentroDoEnvelope(perfil, noVeiculo)) continue

      // ejeção MEDIDA: o corpo terminou fora do envelope. Antes era estimada
      // por ausência de contato, o que não distinguia sair pela porta de parar
      // contra a chapa
      ejetados.add(alvo.pessoa.id)
      let porOnde = 'pelo envelope do veículo'
      for (const colisor of Object.values(alvo.colisores).flat()) {
        world.contactPairsWith(colisor, (outro) => {
          const nome = nomePorColisor.get(outro.handle)
          if (nome) porOnde = nome
        })
      }
      ejecoes.push({ id: alvo.pessoa.id, nome: alvo.pessoa.ancora.nome, tempo: t, porOnde })
    }

    for (const alvo of corposDinamicos) {
      const { id, nome, massaKg, corpo, colisores, referencia, ignorarPrefixo, idOcupante, segmento } = alvo
      const traducao = corpo.translation()
      const rotacao = corpo.rotation()
      trajetorias.get(id)!.push({
        posicao: vetor(traducao.x, traducao.y, traducao.z),
        rotacao: { x: rotacao.x, y: rotacao.y, z: rotacao.z, w: rotacao.w },
      })

      // a leitura é a velocidade RELATIVA ao veículo: é o que o corpo tinha
      // sobrando quando a estrutura já havia desacelerado
      const velocidadeCarga = corpo.linvel()
      const velocidadeVeiculo = velocidadeDoVeiculo(t, referencia)
      const relativaKmh =
        comprimento3(
          subtrair(
            vetor(velocidadeCarga.x, velocidadeCarga.y, velocidadeCarga.z),
            velocidadeVeiculo,
          ),
        ) * 3.6

      if (idOcupante && segmento && ehMembroAprisionavel(segmento)) {
        const contatos = new Set<string>()
        for (const colisor of colisores) {
          world.contactPairsWith(colisor, (outro) => {
            const alvoContato = nomePorColisor.get(outro.handle)
            if (!alvoContato || (ignorarPrefixo && alvoContato.startsWith(ignorarPrefixo))) return
            if (/painel|parede corta-fogo|pedal|coluna b|banco|encosto|assento|porta|estrutura|soleira/i.test(alvoContato)) {
              contatos.add(alvoContato)
            }
          })
        }
        const alvosContato = [...contatos].sort()
        const assinatura = alvosContato.join('|')
        const anteriorContato = contatoSustentado.get(id)
        const passosContato =
          assinatura.length > 0 && anteriorContato?.assinatura === assinatura
            ? anteriorContato.passos + 1
            : assinatura.length > 0
              ? 1
              : 0
        contatoSustentado.set(id, { assinatura, passos: passosContato })

        const evidencia = avaliarPossivelAprisionamento({
          segmento,
          alvos: alvosContato,
          duracaoContatoS: passosContato * passoS,
          velocidadeRelativaMs: relativaKmh / 3.6,
        })
        if (evidencia && !aprisionamentos.has(id)) {
          aprisionamentos.set(id, {
            id,
            idOcupante,
            tempo: t,
            ...evidencia,
          })
        }
      }

      // um pico acima do plausível é artefato de penetração, e é DESCARTADO em
      // vez de aparado: aparar só troca um número impossível por um número
      // grande, e o instrutor não tem como distinguir um do outro
      const tetoPlausivel = velocidadeKmh * FATOR_VELOCIDADE_PLAUSIVEL
      if (relativaKmh > tetoPlausivel) continue
      const serie = historico.get(id)!
      serie.push(relativaKmh)
      if (serie.length <= passosDaJanela) continue

      // velocidade de uma janela atrás: a diferença é a desaceleração do contato
      const anterior = serie[serie.length - 1 - passosDaJanela]!
      const quedaKmh = anterior - relativaKmh
      const melhor = melhorImpacto.get(id)
      if (quedaKmh < QUEDA_MINIMA_IMPACTO_KMH || (melhor && quedaKmh <= melhor.quedaKmh)) continue

      // ENTRE os contatos, o alvo é aquele contra o qual o corpo está indo, e
      // não o primeiro encontrado. O apoio — assento, assoalho — está em contato
      // o tempo todo, e respondia por toda batida: o motorista sem cinto batia
      // no volante e o painel dizia "assento do banco do motorista"
      const direcao = subtrair(
        vetor(velocidadeCarga.x, velocidadeCarga.y, velocidadeCarga.z),
        velocidadeVeiculo,
      )
      const norma = comprimento3(direcao)
      const centroCorpo = corpo.translation()
      let atingido: string | null = null
      let melhorAlinhamento = -Infinity
      for (const colisor of colisores) {
        world.contactPairsWith(colisor, (outro) => {
          const nome = nomePorColisor.get(outro.handle) ?? 'estrutura do veículo'
          // Articulações do próprio ragdoll não são impacto contra o habitáculo.
          if (ignorarPrefixo && nome.startsWith(ignorarPrefixo)) return
          const posicao = outro.translation()
          const para = vetor(
            posicao.x - centroCorpo.x,
            posicao.y - centroCorpo.y,
            posicao.z - centroCorpo.z,
          )
          const distancia = comprimento3(para)
          const alinhamento =
            norma < 1e-6 || distancia < 1e-6
              ? 0
              : (direcao.x * para.x + direcao.y * para.y + direcao.z * para.z) /
                (norma * distancia)
          // só conta o que está À FRENTE do movimento. O teto fica a 3,5 cm da
          // cabeça do adulto sentado nesta maquete e encosta a qualquer solavanco;
          // sem este piso, ele levava o crédito de batidas que aconteceram no
          // volante
          if (alinhamento > ALINHAMENTO_MINIMO_ALVO && alinhamento > melhorAlinhamento) {
            melhorAlinhamento = alinhamento
            atingido = nome
          }
        })
      }
      if (atingido == null) continue

      // a energia é a que o corpo tinha ANTES de ser detido
      const energia = energiaDeMassa(massaKg, anterior)
      melhorImpacto.set(id, {
        id,
        nomeCorpo: nome,
        alvo: atingido,
        tempo: t,
        velocidadeRelativaKmh: anterior,
        joules: energia.joules,
        pesoEquivalenteKgf: energia.pesoEquivalenteKgf,
        quedaKmh,
      })
    }

    if (passo === passos) break
    const proximo = (passo + 1) / passos
    posicionarVeiculo(proximo)
    acionarPretensionadores(proximo)
    acionarAirbags(proximo)
    world.step()
  }

  const impactos = [...melhorImpacto.values()]
    .sort((a, b) => a.tempo - b.tempo)
    .map(({ quedaKmh: _quedaKmh, ...impacto }) => impacto)

  const estresseBiomecanico: Record<string, EstresseBiomecanicoOcupante> = {}
  for (const [id, quadros] of estresseBiomecanicoPorPessoa.entries()) {
    let picoAngulo = 0
    let tempoPico = 0
    let houveSobrecarga = false
    for (let i = 0; i < quadros.length; i++) {
      const q = quadros[i]!
      if (q.anguloCervicalGraus > picoAngulo) {
        picoAngulo = q.anguloCervicalGraus
        tempoPico = i / Math.max(1, quadros.length - 1)
      }
      if (
        q.estresseJoelhos !== 'normal' ||
        q.estresseCotovelos !== 'normal' ||
        q.nivelCervical.classificacao !== 'fisiologico'
      ) {
        houveSobrecarga = true
      }
    }
    estresseBiomecanico[id] = {
      idOcupante: id,
      quadros,
      picoAnguloCervical: picoAngulo,
      tempoPico,
      houveSobrecargaArticular: houveSobrecarga,
    }
  }

  const saida: ResultadoFisica = {
    trajetorias: [...trajetorias.entries()].map(([id, quadros]) => ({ id, quadros })),
    trajetoriaAlvo: alvosExternos[0]
      ? { id: ID_ALVO_EXTERNO, quadros: alvosExternos[0].quadros }
      : null,
    trajetoriaAlvoSecundario: alvosExternos[1]
      ? { id: ID_ALVO_EXTERNO_SECUNDARIO, quadros: alvosExternos[1].quadros }
      : null,
    impactos,
    aprisionamentos: [...aprisionamentos.values()].sort((a, b) => a.tempo - b.tempo),
    ejecoes,
    estresseBiomecanico,
    passoS,
    duracaoS,
  }

  world.free()
  return saida
}
